import { TagColorSchemes } from '@paywithextend/components'
import moment from 'moment'
import { Moment } from 'moment-timezone'
import React from 'react'
import { OrgExpenseReportCycleType, OrgExpenseReportType } from 'src/enums'
import Trns from 'src/localization/Trns'
import { convertUTCRecurringLabel } from 'src/utils/date.utils'

export interface OrgExpenseReportCycle {
  id: string
  issuerName: string
  reportType: OrgExpenseReportType
  cycleType?: OrgExpenseReportCycleType
  dayOfMonth?: number
  currentReportId?: string
  currentReportEndDate?: Date
  previousReportId?: string
  previousReportEndDate?: Date
  accountId: string
  accountName: string
  accountLast4: number
  accountTimezone?: string
  accountCurrency: string
  accountUserId: string
  accountUserFullName: string
  accountUserFirstName: string
  accountUserLastName: string
  accountUserEmail: string
  accountUserAvatarUrl?: string
}

export class OrgExpenseReportCycleModel {
  public id: string
  public issuerName: string
  public reportType: OrgExpenseReportType
  public cycleType?: OrgExpenseReportCycleType
  public dayOfMonth?: number
  public currentReportId?: string
  public currentReportEndDate?: Date
  public previousReportId?: string
  public previousReportEndDate?: Date
  public accountId: string
  public accountName: string
  public accountLast4: number
  public accountTimezone?: string
  public accountCurrency: string
  public accountUserId: string
  public accountUserFullName: string
  public accountUserFirstName: string
  public accountUserLastName: string
  public accountUserEmail: string
  public accountUserAvatarUrl?: string

  public constructor(data: OrgExpenseReportCycle) {
    this.id = data.id
    this.issuerName = data.issuerName
    this.reportType = data.reportType
    this.cycleType = data.cycleType
    this.dayOfMonth = data.dayOfMonth
    this.currentReportId = data.currentReportId
    this.currentReportEndDate = data.currentReportEndDate
    this.previousReportId = data.previousReportId
    this.previousReportEndDate = data.previousReportEndDate
    this.accountId = data.accountId
    this.accountName = data.accountName
    this.accountLast4 = data.accountLast4
    this.accountTimezone = data.accountTimezone
    this.accountCurrency = data.accountCurrency
    this.accountUserId = data.accountUserId
    this.accountUserFullName = data.accountUserFullName
    this.accountUserFirstName = data.accountUserFirstName
    this.accountUserLastName = data.accountUserLastName
    this.accountUserEmail = data.accountUserEmail
    this.accountUserAvatarUrl = data.accountUserAvatarUrl
  }

  public get cycleLabel(): React.ReactElement {
    switch (this.cycleType) {
      case OrgExpenseReportCycleType.DayOfMonth:
        return (
          <Trns
            source={'${day} of every month'}
            expressions={{
              day: convertUTCRecurringLabel(
                `${this.dayOfMonth}-01-3000`,
                'DD-MM-YYYY',
                this.accountTimezone || 'UTC',
              ).format('Do'),
            }}
          />
        )
      case OrgExpenseReportCycleType.Monthly:
        return <Trns source={'Last day of the month'} />
      default:
        return <Trns source={'Set up statement'} />
    }
  }
}

export interface OrgExpenseReportCycleGroup {
  issuerName: string
  rcns: OrgExpenseReportCycleModel[]
}

export enum OrgExpenseReportStatus {
  Flagged = 'FLAGGED',
  PendingReview = 'PENDING_REVIEW',
  Reviewed = 'REVIEWED',
  ReportInProgress = 'REPORT_IN_PROGRESS',
}

export function orgExpenseReportStatusToString(
  status: OrgExpenseReportStatus,
): string {
  switch (status) {
    case OrgExpenseReportStatus.Flagged:
      return 'Flagged'
    case OrgExpenseReportStatus.PendingReview:
      return 'Pending Review'
    case OrgExpenseReportStatus.Reviewed:
      return 'Reviewed'
    case OrgExpenseReportStatus.ReportInProgress:
      return 'Report In Progress'
  }
}

export function orgExpenseReportStatusToColor(
  status: OrgExpenseReportStatus,
): TagColorSchemes {
  switch (status) {
    case OrgExpenseReportStatus.Flagged:
      return TagColorSchemes.Red
    case OrgExpenseReportStatus.PendingReview:
      return TagColorSchemes.Yellow
    case OrgExpenseReportStatus.Reviewed:
      return TagColorSchemes.Green
    case OrgExpenseReportStatus.ReportInProgress:
      return TagColorSchemes.Default
  }
}

export function getOrgExpenseReportStatus(
  report: OrgExpenseReport,
  metrics: OrgExpenseReportMetrics,
): OrgExpenseReportStatus {
  if (moment.utc(report.until).isSameOrAfter(moment.utc(), 'day')) {
    return OrgExpenseReportStatus.ReportInProgress
  } else if (
    metrics.transactionsReviewedCount === metrics.transactionsTotalCount &&
    moment.utc(report.until).isBefore(moment.utc(), 'day')
  ) {
    return OrgExpenseReportStatus.Reviewed
  } else {
    return OrgExpenseReportStatus.PendingReview
  }
}

export interface OrgExpenseReport {
  id: string
  cycleId: string
  since: Date
  until: Date
  accountId: string
  accountName: string
  accountLast4: number
  accountTimezone?: string
  accountCurrency: string
  accountUserId: string
  accountUserFullName: string
  accountUserFirstName: string
  accountUserLastName: string
  accountUserEmail: string
  accountUserAvatarUrl?: string
  reportStatus?: OrgExpenseReportStatus
  totalSpentCents?: number
  physicalCardTotalSpentCents?: number
  virtualCardTotalSpentCents?: number
  billPayCardsTotalSpentCents?: number
}

export interface OrgExpenseReportMetrics {
  id: string
  totalSpentCents: number
  physicalCardTotalSpentCents: number
  virtualCardSpentCents: number
  billPaySpentCents: number
  transactionsReviewedCount: number
  transactionsTotalCount: number
  missingReceiptsCount: number
  missingCategoriesCount?: number
}

export enum OrgExpenseReportConfirmDateChangePopup {
  MergeReports = 'ORG_EXPENSE_REPORT_MERGE_REPORTS',
  CreateNewReport = 'ORG_EXPENSE_REPORT_CREATE_NEW_REPORT',
}

export interface OrgExpenseReportConfirmDateChangePopupProps {
  type: OrgExpenseReportConfirmDateChangePopup
  date: Moment
  rcnName: string
}
